import {
  Box,
  Avatar,
  Flex,
  Heading,
  Text,
  Button,
  Spacer,
  Spinner,
  useColorModeValue
} from '@chakra-ui/react';
import { UserModel } from 'models';

export interface BasicUserCardProps {
  user: UserModel;
  onClick?: () => void;
  onUserTextClick?: () => void;
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  titleSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  boldTitle?: boolean;
  showEmail?: boolean;
  showButton?: boolean;
  buttonText?: string;
  buttonIcon?: JSX.Element;
  onButtonClick?: () => void;
  buttonLoading?: boolean;
  showBorder?: boolean;
  disabled?: boolean;
  selected?: boolean;
}

export enum BasicUserCardSizingConfig {
  CARD_HEIGHT = '60px',
  PANEL_MIN_HEIGHT = '80px'
}

export enum BasicUserCardStylingConfig {
  BORDER_COLOR_LIGHT = '#f1f3f7',
  BORDER_COLOR_DARK = '#3f434c'
}

export default function BasicUserCard({
  user,
  onClick,
  onUserTextClick,
  onButtonClick,
  buttonIcon,
  avatarSize = 'sm',
  titleSize = 'md',
  buttonText = '',
  boldTitle = true,
  showEmail = true,
  showButton = false,
  buttonLoading = false,
  showBorder = true,
  disabled = false,
  selected = false
}: BasicUserCardProps) {
  const borderColor = useColorModeValue(BasicUserCardStylingConfig.BORDER_COLOR_LIGHT, BasicUserCardStylingConfig.BORDER_COLOR_DARK);
  const boxColor = useColorModeValue('gray.200', 'gray.800');

  function renderUserEmail() {
    if (showEmail && user.email) {
      return (
        <Text fontSize='xs' fontWeight='light' color="gray.500">
          {user.email}
        </Text>
      );
    }
  }

  function renderUserName() {
    const displayName = user.userName || user.name || user.email;
    const contentProps = {
      _hover: onUserTextClick ? {color: 'blue.500'} : {},
      fontSize: titleSize,
      cursor: onUserTextClick ? 'pointer' : '',
      onClick: onUserTextClick
    }

    if (boldTitle) {
      return (
        <Heading {...contentProps}>
          {displayName}
        </Heading>
      );
    } else {
      return (
        <Text {...contentProps}>
          {displayName}
        </Text>
      );
    }
  }

  function renderUserButton() {
    const buttonLeftIcon = buttonLoading ? <Spinner size="sm" /> : buttonIcon;
    if (showButton) {
      return (
        <Button leftIcon={buttonLeftIcon} border="1px" onClick={onButtonClick} maxWidth="33%">
          {buttonText}
        </Button>
      );
    }
  }

  const displayName = user.userName || user.name || user.email;

  return (
    <Flex
      direction='row'
      alignItems='center'
      gap='8px'
      minHeight={BasicUserCardSizingConfig.CARD_HEIGHT}
      width="100%"
      overflow='hidden'
      onClick={onClick}
      cursor={onClick && !disabled ? 'pointer' : ''}
      padding={showBorder ? '10px' : '10px 0px'}
      border={showBorder ? "1px solid" : 'none'}
      borderColor={showBorder ? borderColor : undefined}
      backgroundColor={selected ? boxColor : ''}
      _hover={onClick && !disabled ? { backgroundColor: boxColor } : {}}
    >
      <Avatar
        marginRight="16px"
        size={avatarSize}
        name={displayName}
        src={user.image || undefined}
      />
      <Flex direction="column" textAlign="left">
        {renderUserName()}
        {renderUserEmail()}
      </Flex>
      <Spacer />
      {renderUserButton()}
    </Flex>
  );
}
